import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getJobCron, getJobStatus, startJob, stopJob, updateJobCron } from '@/features/shared/services/jobService';
const statusLabelMap = {
    running: 'En cours',
    success: 'Succès',
    failure: 'Échec',
};
const statusClassMap = {
    running: 'bg-blue-100 text-blue-800',
    success: 'bg-green-100 text-green-800',
    failure: 'bg-red-100 text-red-800',
};
const cronErrorMessages = {
    CRON_REQUIRED: "L'expression CRON est obligatoire",
    INVALID_CRON_EXPRESSION: 'Expression CRON invalide',
    JOB_NOT_FOUND: 'Job introuvable',
};
const formatDateTime = (value) => {
    if (!value) {
        return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return new Intl.DateTimeFormat('fr-FR', {
        dateStyle: 'short',
        timeStyle: 'short',
    }).format(date);
};
const extractErrorMessage = (error, fallback) => {
    if (error instanceof Error && error.message) {
        return error.message;
    }
    if (typeof error === 'object' && error !== null && 'message' in error) {
        const message = error.message;
        if (message) {
            return message;
        }
    }
    return fallback;
};
const buildCronErrorMessage = (code, fallbackMessage) => {
    if (!code) {
        return fallbackMessage ?? 'Impossible de mettre à jour la planification';
    }
    const baseMessage = cronErrorMessages[code] ?? fallbackMessage ?? 'Erreur lors de la mise à jour de la planification';
    return baseMessage.includes(code) ? baseMessage : `${baseMessage} (${code})`;
};
const JobControlCard = ({ jobKey, title, description, className = '', startButtonLabel = 'Activer', stopButtonLabel = 'Désactiver', refreshButtonLabel = 'Actualiser', activeLabel = 'Actif', inactiveLabel = 'Inactif', startSuccessMessage = 'Job activé', stopSuccessMessage = 'Job désactivé', startErrorMessage = 'Impossible d\'activer le job', stopErrorMessage = 'Impossible de désactiver le job', cronConfig, }) => {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [cronMetadata, setCronMetadata] = useState(null);
    const [cronExpression, setCronExpression] = useState(cronConfig?.defaultExpression ?? '');
    const [cronError, setCronError] = useState(null);
    const [cronLoading, setCronLoading] = useState(Boolean(cronConfig));
    const [cronSaving, setCronSaving] = useState(false);
    const cronEnabled = Boolean(cronConfig);
    const cronDefaultExpression = cronConfig?.defaultExpression ?? '';
    const cronLabel = cronConfig?.label ?? 'Expression CRON';
    const cronSubmitLabel = cronConfig?.submitLabel ?? 'Enregistrer';
    const cronSuccessMessage = cronConfig?.successMessage ?? 'Planification mise à jour';
    const cronHelperText = cronConfig?.helperText ?? (_jsxs("span", { children: ["Utilisez une expression CRON valide. Valeur par d\u00E9faut :", ' ', _jsx("code", { className: "rounded bg-gray-200 px-1 py-0.5 text-xs", children: cronDefaultExpression })] }));
    useEffect(() => {
        if (cronEnabled && !cronMetadata) {
            setCronExpression(cronDefaultExpression);
        }
    }, [cronEnabled, cronDefaultExpression, cronMetadata]);
    const fetchStatus = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getJobStatus(jobKey);
            setStatus(data);
        }
        catch (error) {
            const message = extractErrorMessage(error, 'Erreur lors de la récupération du statut du job');
            toast.error(`Erreur : ${message}`);
        }
        finally {
            setLoading(false);
        }
    }, [jobKey]);
    const mergeStatusWithMetadata = useCallback((metadata) => {
        setStatus((previous) => {
            if (!previous) {
                return {
                    name: metadata.name,
                    is_active: metadata.is_active,
                    last_run_at: metadata.last_run_at ?? null,
                    next_run_at: metadata.next_run_at ?? null,
                    last_status: metadata.last_status ?? null,
                };
            }
            return {
                ...previous,
                is_active: metadata.is_active,
                ...(metadata.last_run_at !== undefined ? { last_run_at: metadata.last_run_at ?? null } : {}),
                ...(metadata.next_run_at !== undefined ? { next_run_at: metadata.next_run_at ?? null } : {}),
                ...(metadata.last_status !== undefined ? { last_status: metadata.last_status ?? null } : {}),
            };
        });
    }, []);
    useEffect(() => {
        void fetchStatus();
    }, [fetchStatus]);
    const fetchCron = useCallback(async () => {
        if (!cronEnabled) {
            return;
        }
        try {
            setCronLoading(true);
            const data = await getJobCron(jobKey);
            setCronMetadata(data);
            const value = data.cron && data.cron.trim() ? data.cron : cronDefaultExpression;
            setCronExpression(value);
            setCronError(null);
            mergeStatusWithMetadata(data);
        }
        catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 404) {
                setCronMetadata({
                    name: jobKey,
                    cron: cronDefaultExpression,
                    is_active: false,
                });
                setCronExpression(cronDefaultExpression);
                setCronError(null);
                return;
            }
            const message = extractErrorMessage(error, 'Erreur lors de la récupération de la planification du job');
            toast.error(`Erreur : ${message}`);
            setCronExpression(cronDefaultExpression);
        }
        finally {
            setCronLoading(false);
        }
    }, [cronEnabled, cronDefaultExpression, jobKey, mergeStatusWithMetadata]);
    useEffect(() => {
        if (cronEnabled) {
            void fetchCron();
        }
    }, [cronEnabled, fetchCron]);
    const handleStart = async () => {
        try {
            setActionLoading(true);
            const data = await startJob(jobKey);
            setStatus(data);
            toast.success(startSuccessMessage);
        }
        catch (error) {
            const message = extractErrorMessage(error, startErrorMessage);
            toast.error(`Erreur : ${message}`);
        }
        finally {
            setActionLoading(false);
        }
    };
    const handleStop = async () => {
        try {
            setActionLoading(true);
            const data = await stopJob(jobKey);
            setStatus(data);
            toast.success(stopSuccessMessage);
        }
        catch (error) {
            const message = extractErrorMessage(error, stopErrorMessage);
            toast.error(`Erreur : ${message}`);
        }
        finally {
            setActionLoading(false);
        }
    };
    const handleCronChange = (event) => {
        setCronExpression(event.target.value);
        if (cronError) {
            setCronError(null);
        }
    };
    const handleCronSubmit = async (event) => {
        if (!cronEnabled) {
            return;
        }
        event.preventDefault();
        setCronError(null);
        const trimmedCron = cronExpression.trim();
        if (!trimmedCron) {
            setCronError(buildCronErrorMessage('CRON_REQUIRED'));
            return;
        }
        try {
            setCronSaving(true);
            const data = await updateJobCron(jobKey, trimmedCron);
            setCronMetadata(data);
            const nextValue = data.cron && data.cron.trim() ? data.cron : trimmedCron;
            setCronExpression(nextValue);
            mergeStatusWithMetadata(data);
            setCronError(null);
            toast.success(cronSuccessMessage);
            const hasScheduleMetadata = 'last_run_at' in data || 'next_run_at' in data || 'last_status' in data;
            if (!hasScheduleMetadata) {
                void fetchStatus();
            }
        }
        catch (error) {
            let feedback;
            if (axios.isAxiosError(error)) {
                const responseData = error.response?.data;
                const code = responseData?.code ?? responseData?.error_code;
                const apiMessage = responseData?.message;
                feedback = buildCronErrorMessage(code, apiMessage);
            }
            else {
                feedback = extractErrorMessage(error, 'Impossible de mettre à jour la planification');
            }
            setCronError(feedback);
        }
        finally {
            setCronSaving(false);
        }
    };
    const isLoading = loading || cronLoading;
    if (isLoading) {
        return (_jsx("div", { className: `rounded-lg border bg-white p-4 shadow-sm ${className}`, children: "Chargement..." }));
    }
    if (!status) {
        return (_jsx("div", { className: `rounded-lg border bg-white p-4 shadow-sm ${className}`, children: "Impossible de r\u00E9cup\u00E9rer le statut du job" }));
    }
    const lastStatus = status.last_status ? status.last_status.toLowerCase() : null;
    const lastStatusLabel = lastStatus ? (statusLabelMap[lastStatus] ?? status.last_status) : '—';
    const lastStatusClass = lastStatus ? (statusClassMap[lastStatus] ?? 'bg-gray-100 text-gray-800') : 'bg-gray-100 text-gray-800';
    return (_jsxs("div", { className: `space-y-4 rounded-lg border bg-white p-6 shadow-sm ${className}`, children: [_jsxs("div", { className: "flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between", children: [_jsxs("div", { children: [_jsx("h2", { className: "text-lg font-semibold text-gray-900", children: title }), description && _jsx("p", { className: "mt-1 text-sm text-gray-600", children: description })] }), _jsxs("div", { className: "mt-2 flex items-center gap-2 sm:mt-0", children: [_jsx("span", { className: "text-sm font-medium text-gray-700", children: "Dernier statut :" }), _jsx("span", { className: `rounded-full px-2 py-1 text-xs font-semibold uppercase ${lastStatusClass}`, children: lastStatusLabel })] })] }), _jsxs("div", { className: "space-y-2 text-sm text-gray-700", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "font-medium", children: "\u00C9tat :" }), _jsx("span", { className: status.is_active ? 'text-green-600' : 'text-red-600', children: status.is_active ? activeLabel : inactiveLabel })] }), _jsxs("div", { children: [_jsx("span", { className: "font-medium", children: "Derni\u00E8re ex\u00E9cution :" }), " ", formatDateTime(status.last_run_at)] }), _jsxs("div", { children: [_jsx("span", { className: "font-medium", children: "Prochaine ex\u00E9cution :" }), " ", formatDateTime(status.next_run_at)] })] }), cronEnabled && (_jsxs("form", { className: "space-y-3 rounded-md border border-gray-200 bg-gray-50 p-4", onSubmit: handleCronSubmit, children: [_jsxs("div", { children: [_jsx("label", { htmlFor: `${jobKey}-cron`, className: "block text-sm font-medium text-gray-700", children: cronLabel }), _jsx("input", { id: `${jobKey}-cron`, type: "text", value: cronExpression, onChange: handleCronChange, placeholder: cronDefaultExpression, disabled: cronLoading || cronSaving, className: "mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100" }), cronHelperText && (_jsx("p", { className: "mt-2 text-xs text-gray-500", children: cronHelperText })), cronError && _jsx("p", { className: "mt-2 text-sm text-red-600", children: cronError })] }), _jsxs("div", { className: "flex flex-col gap-2 sm:flex-row", children: [_jsx("button", { type: "submit", disabled: cronLoading || cronSaving, className: "inline-flex items-center justify-center rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60", children: cronSubmitLabel }), _jsx("button", { type: "button", onClick: () => {
                                    const value = cronMetadata?.cron && cronMetadata.cron.trim()
                                        ? cronMetadata.cron
                                        : cronDefaultExpression;
                                    setCronExpression(value);
                                    setCronError(null);
                                }, disabled: cronLoading || cronSaving, className: "inline-flex items-center justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60", children: "R\u00E9initialiser" })] })] })), _jsxs("div", { className: "flex flex-col gap-2 sm:flex-row", children: [_jsx("button", { type: "button", onClick: handleStart, disabled: status.is_active || actionLoading, className: "inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60", children: startButtonLabel }), _jsx("button", { type: "button", onClick: handleStop, disabled: !status.is_active || actionLoading, className: "inline-flex items-center justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60", children: stopButtonLabel }), _jsx("button", { type: "button", onClick: () => void fetchStatus(), disabled: actionLoading, className: "inline-flex items-center justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60", children: refreshButtonLabel })] })] }));
};
export default JobControlCard;
